import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, MessageCircle } from "lucide-react";
import SiteNav from "@/components/SiteNav";
import SiteFooter from "@/components/SiteFooter";
import WhatsAppFab from "@/components/WhatsAppFab";

const WA = "https://wa.me/60183628663";

const SHARED_EN = {
    faq: [
        { q: "Where do you shoot?", a: "KL, Selangor, PJ and surrounding areas — included in the price. Outstation (Penang / JB) by quote." },
        { q: "How fast is delivery?", a: "Each project's delivery timeline is confirmed in the written quotation before the deposit is paid." },
        { q: "What does it cost?", a: "Current packages and prices are on the services page — click 'See packages & pricing' below." },
        { q: "What is the payment flow?", a: "50% deposit confirms the date, 50% on delivery. Bank transfer, Stripe or TNG." },
        { q: "Who owns the content?", a: "You retain organic social media usage rights for the delivered content. Paid advertising, broadcast and out-of-home placements are licensed separately in writing." },
    ],
    process: [
        { t: "Brand DNA brief", d: "A 30-minute pre-call on palette, mood, menu heroes and references." },
        { t: "Half-day shoot", d: "On-location in KL & Selangor. Shot personally by Kaily." },
        { t: "Edit & grade", d: "Hand-edited, colour-graded, social-ready crops for every deliverable." },
        { t: "Delivery", d: "Cloud gallery link. Timeline, revisions and storage window are set in the written quotation. Organic social usage included; paid-media licensing arranged separately in writing." },
    ],
    labels: { process: "How it works", faq: "Frequently asked", cta: "WhatsApp Kaily", pkg: "See packages & pricing", work: "See selected work", also: "Also looking for" },
};
const SHARED_CN = {
    faq: [
        { q: "在哪里拍摄?", a: "吉隆坡、雪兰莪、PJ 及周边已含在价格内。槟城 / 新山另行报价。" },
        { q: "多快交付?", a: "每个项目的交付时间以预订前书面报价为准。" },
        { q: "价格多少?", a: "最新套餐与价格请见服务页,点击下方「查看套餐与价格」。" },
        { q: "怎么付款?", a: "50% 订金锁定档期,交付时付尾款。支持银行转账 / Stripe / TNG。" },
        { q: "内容版权归谁?", a: "交付内容的自然社媒发布使用权归你。付费广告、广播及户外投放授权另行书面约定。" },
    ],
    process: [
        { t: "品牌 DNA 简报", d: "30 分钟前期沟通:色调、氛围、菜单主角与参考。" },
        { t: "半天实地拍摄", d: "吉隆坡与雪兰莪实地拍摄,Kaily 亲自掌镜。" },
        { t: "剪辑与调色", d: "手工剪辑、电影感调色,每项交付附社媒即用尺寸。" },
        { t: "交付", d: "云端相册链接。交付时间、修改与储存期限以书面报价为准。含自然社媒发布使用权;付费广告授权另行书面约定。" },
    ],
    labels: { process: "流程", faq: "常见问题", cta: "WhatsApp 联系 Kaily", pkg: "查看套餐与价格", work: "查看作品", also: "你可能也在找" },
};

export const INTENT_PAGES = {
    "food-videographer-kuala-lumpur": {
        en: {
            kicker: "Food videographer · Kuala Lumpur & Selangor",
            h1: "Cinematic food videos that make people taste the screen.",
            sub: "Kaily is an independent food videographer serving KL, PJ and the Klang Valley — short Reels, shop videos and menu films for cafes, restaurants and F&B brands. Current packages and delivery timelines are listed on the services page.",
            points: ["Cafe & restaurant Reels (vertical, 15–30s)", "Cinematic shop videos that show how your space feels", "Menu films that make dishes move", "Shot, edited and colour-graded personally by Kaily"],
        },
        cn: {
            kicker: "餐饮视频拍摄 · 吉隆坡 & 雪兰莪",
            h1: "让人隔着屏幕就想吃的电影感餐饮视频。",
            sub: "Kaily 是服务吉隆坡、PJ 与巴生谷的独立餐饮视频摄影师 — 咖啡馆与餐厅短视频 Reels、店铺视频与菜单影片。最新套餐与交付时间请见服务页。",
            points: ["咖啡馆与餐厅 Reels(竖版,15–30 秒)", "呈现空间氛围的电影感店铺视频", "让菜品动起来的菜单影片", "Kaily 亲自拍摄、剪辑与调色"],
        },
    },
    "restaurant-opening-campaign-kl": {
        en: {
            kicker: "Opening campaign · Kuala Lumpur",
            h1: "Open your restaurant with content that fills tables from day one.",
            sub: "A complete opening campaign in one engagement: launch reel, menu photography and a bank of social-ready content — planned around your opening date in KL or Selangor.",
            points: ["Launch reel timed to your opening announcement", "Menu photography ready for delivery platforms & socials", "A 30-day content bank so your feed never opens empty", "One shoot day — minimal disruption before opening"],
        },
        cn: {
            kicker: "开业宣传拍摄 · 吉隆坡",
            h1: "用内容为开业第一天填满座位。",
            sub: "一次完成整套开业宣传:开业短视频、菜单摄影与一批社媒即用内容 — 围绕你在吉隆坡或雪兰莪的开业日期规划。",
            points: ["配合开业官宣的开业短视频", "可直接用于外卖平台与社媒的菜单摄影", "30 天内容储备,开业后不断更", "只需一个拍摄日,开业前干扰最小"],
        },
    },
    "food-product-video-malaysia": {
        en: {
            kicker: "Product video · Malaysia",
            h1: "Product videos that make packaged food look irresistible.",
            sub: "Product reels, packaging shots and brand films for Malaysian F&B brands — bottled drinks, snacks, sauces, desserts and more. Shot in KL & Selangor; usable nationwide for organic social. Paid-media licensing arranged separately in writing.",
            points: ["Product Reels built for Instagram & TikTok", "Packaging and texture close-ups that sell shelf appeal", "Brand films for websites and campaigns", "Organic social usage included"],
        },
        cn: {
            kicker: "产品视频 · 马来西亚",
            h1: "让包装食品令人无法抗拒的产品视频。",
            sub: "为马来西亚餐饮品牌拍摄产品短视频、包装镜头与品牌影片 — 瓶装饮料、零食、酱料、甜品等。在吉隆坡与雪兰莪拍摄,含自然社媒发布使用权。付费广告授权另行书面约定。",
            points: ["为 Instagram 与 TikTok 设计的产品 Reels", "呈现质感与货架吸引力的包装特写", "适用于官网与广告的品牌影片", "含自然社媒发布使用权"],
        },
    },
    "menu-photographer-klang-valley": {
        en: {
            kicker: "Menu photography · Klang Valley",
            h1: "Menu photos that sell the dish before the price is read.",
            sub: "Hand-retouched menu photography for cafes and restaurants across the Klang Valley — menu-ready aspect ratios plus social crops for every image.",
            points: ["Menu-ready aspect ratios for print & delivery apps", "Feed + story crops included for every image", "Natural-light-first styling that keeps food looking real", "Cloud gallery delivery"],
        },
        cn: {
            kicker: "菜单摄影 · 巴生谷",
            h1: "让客人还没看价格就想点的菜单照片。",
            sub: "为巴生谷咖啡馆与餐厅提供手工精修菜单摄影 — 每张图附菜单即用比例与社媒尺寸。",
            points: ["适配印刷与外卖平台的菜单比例", "每张图附 Feed + Story 尺寸", "自然光优先,让食物保持真实质感", "云端相册交付"],
        },
    },
};

export default function IntentPage({ slug, lang = "en" }) {
    const page = INTENT_PAGES[slug]?.[lang] || INTENT_PAGES[slug]?.en;
    const shared = lang === "cn" ? SHARED_CN : SHARED_EN;

    useEffect(() => {
        if (!page) return;
        const siteBase = window.location.origin;
        const canonical = `https://slowcafe.my/${slug}${lang === "cn" ? "/cn" : ""}`;
        const title = `${page.h1.replace(/[。.]+$/, "")} · Kaily F&B Visual Studio`;
        const desc = page.sub;
        const image = `${siteBase}/og-default.jpg`;
        const ensure = (sel, make) => {
            let el = document.head.querySelector(sel);
            if (!el) { el = make(); document.head.appendChild(el); }
            return el;
        };
        const setMeta = (prop, name, content) => {
            const el = ensure(`meta[${prop}="${name}"]`, () => {
                const m = document.createElement("meta");
                m.setAttribute(prop, name);
                return m;
            });
            el.setAttribute("content", content);
        };
        const prevTitle = document.title;
        document.title = title;
        setMeta("name", "description", desc);
        setMeta("name", "robots", "index, follow");
        setMeta("property", "og:type", "website");
        setMeta("property", "og:url", canonical);
        setMeta("property", "og:title", title);
        setMeta("property", "og:description", desc);
        setMeta("property", "og:image", image);
        setMeta("name", "twitter:card", "summary_large_image");
        const canon = ensure('link[rel="canonical"]', () => Object.assign(document.createElement("link"), { rel: "canonical" }));
        canon.setAttribute("href", canonical);
        // Service JSON-LD for discovery
        let ld = document.getElementById("intent-jsonld");
        if (!ld) { ld = document.createElement("script"); ld.type = "application/ld+json"; ld.id = "intent-jsonld"; document.head.appendChild(ld); }
        ld.textContent = JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "name": page.kicker,
            "url": canonical,
            "description": desc,
            "areaServed": { "@type": "AdministrativeArea", "name": "Klang Valley, Malaysia" },
            "provider": {
                "@type": "LocalBusiness",
                "name": "Kaily F&B Visual Studio",
                "url": "https://slowcafe.my",
                "image": image,
                "telephone": "+60183628663",
                "address": { "@type": "PostalAddress", "addressRegion": "Selangor", "addressCountry": "MY" },
                "sameAs": ["https://www.instagram.com/kaily_gan/"],
            },
            "offers": { "@type": "AggregateOffer", "priceCurrency": "MYR", "lowPrice": "388" },
        });
        return () => { document.title = prevTitle; };
    }, [slug, lang, page]);

    if (!page) return null;
    const others = Object.keys(INTENT_PAGES).filter((s) => s !== slug);
    const otherTitle = (s) => INTENT_PAGES[s][lang === "cn" ? "cn" : "en"].kicker;
    const suffix = lang === "cn" ? "/cn" : "";
    return (
        <div className="min-h-screen bg-cafe-cream text-cafe-espresso" data-testid={`intent-page-${slug}`}>
            <SiteNav />
            <main className="pt-28 md:pt-36 pb-24">
                <section className="max-w-5xl mx-auto px-6 md:px-12">
                    <div className="text-xs md:text-sm uppercase tracking-[0.25em] text-cafe-olive font-semibold mb-5">{page.kicker}</div>
                    <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-balance max-w-4xl">{page.h1}</h1>
                    <p className="mt-6 text-base md:text-lg text-cafe-mocha max-w-2xl leading-relaxed">{page.sub}</p>
                    <div className="mt-8 flex flex-wrap gap-3">
                        <a href={WA} target="_blank" rel="noopener noreferrer" data-testid="intent-cta-whatsapp"
                            className="inline-flex items-center gap-2 h-12 px-6 rounded-full bg-cafe-olive text-cafe-cream hover:bg-cafe-oliveDark transition-colors">
                            <MessageCircle className="h-4 w-4" strokeWidth={1.75} />{shared.labels.cta}
                        </a>
                        <Link to="/photo-video-services" data-testid="intent-cta-packages"
                            className="inline-flex items-center gap-2 h-12 px-6 rounded-full border border-cafe-espresso/30 hover:border-cafe-olive hover:text-cafe-olive transition-colors">
                            {shared.labels.pkg}<ArrowRight className="h-4 w-4" />
                        </Link>
                    </div>
                    <ul className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="intent-points">
                        {page.points.map((p, i) => (
                            <li key={i} className="flex items-start gap-3 bg-white/60 border border-border rounded-2xl p-5">
                                <Check className="h-5 w-5 text-cafe-olive mt-0.5 shrink-0" strokeWidth={2} />
                                <span className="text-sm md:text-base">{p}</span>
                            </li>
                        ))}
                    </ul>
                </section>

                <section className="max-w-5xl mx-auto px-6 md:px-12 mt-20">
                    <h2 className="font-serif text-2xl md:text-3xl mb-8">{shared.labels.process}</h2>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-5">
                        {shared.process.map((s, i) => (
                            <div key={i} className="border border-border rounded-2xl p-5 bg-white/40">
                                <div className="text-cafe-olive font-serif text-2xl">{String(i + 1).padStart(2, "0")}</div>
                                <div className="mt-2 font-medium">{s.t}</div>
                                <p className="mt-1 text-sm text-cafe-mocha leading-relaxed">{s.d}</p>
                            </div>
                        ))}
                    </div>
                </section>

                <section className="max-w-5xl mx-auto px-6 md:px-12 mt-20" data-testid="intent-faq">
                    <h2 className="font-serif text-2xl md:text-3xl mb-6">{shared.labels.faq}</h2>
                    <div className="divide-y divide-border border-y border-border">
                        {shared.faq.map((f, i) => (
                            <details key={i} className="py-4 group">
                                <summary className="cursor-pointer select-none font-medium flex items-center justify-between">
                                    {f.q}<span className="text-cafe-olive group-open:rotate-45 transition-transform text-xl leading-none">+</span>
                                </summary>
                                <p className="mt-2 text-sm text-cafe-mocha leading-relaxed">{f.a}</p>
                            </details>
                        ))}
                    </div>
                </section>

                <section className="max-w-5xl mx-auto px-6 md:px-12 mt-16 text-sm text-cafe-mocha">
                    <div className="uppercase tracking-[0.2em] text-xs text-cafe-olive mb-3">{shared.labels.also}</div>
                    <div className="flex flex-wrap gap-x-6 gap-y-2">
                        {others.map((s) => (
                            <Link key={s} to={`/${s}${suffix}`} className="hover:text-cafe-olive underline underline-offset-4">{otherTitle(s)}</Link>
                        ))}
                        <Link to="/work" className="hover:text-cafe-olive underline underline-offset-4">{shared.labels.work}</Link>
                    </div>
                </section>
            </main>
            <SiteFooter />
            <WhatsAppFab />
        </div>
    );
}
